import { useCallback, useEffect, useRef, useState } from 'react';
import { AccessTokenState, Space, User } from '@core/types/state';

export type UseFetchAssignmentsParams = {
  accessTokens: AccessTokenState;
  fetchAccessToken: ({
    spaceId,
  }: {
    spaceId: string;
  }) => Promise<string | null>;
  fetchAssignments: (params: {
    user: User | null;
    accessToken: string;
    spaceId: string;
  }) => void;
  isFetching: boolean;
  needsToRefreshAccessTokenProp: () => boolean;
  selectedSpace?: Space;
  user: User | null;
};

/**
 * Resolves an access token for the selected space and loads its assignments.
 * Shared by the assignments and submissions responsive pages so both render the
 * same sidebar list without duplicating the token refresh logic.
 */
const useFetchAssignments = ({
  accessTokens,
  fetchAccessToken,
  fetchAssignments,
  isFetching,
  needsToRefreshAccessTokenProp,
  selectedSpace,
  user,
}: UseFetchAssignmentsParams) => {
  const selectedSpaceId = selectedSpace?.objectId;
  const [refreshVersion, setRefreshVersion] = useState(0);
  const requested = useRef(true);
  const resolvingToken = useRef(false);
  const generation = useRef(0);
  const latest = useRef({ user, fetchAssignments });
  latest.current = { user, fetchAssignments };

  const requestRefresh = useCallback(() => {
    requested.current = true;
    setRefreshVersion((value) => value + 1);
  }, []);

  useEffect(() => {
    requested.current = true;
    resolvingToken.current = false;
    return () => {
      generation.current += 1;
    };
  }, [selectedSpaceId]);

  useEffect(() => {
    const resolveAccessToken = async (spaceId: string) => {
      if (needsToRefreshAccessTokenProp()) {
        return fetchAccessToken({ spaceId });
      }

      return accessTokens[spaceId]?.token || fetchAccessToken({ spaceId });
    };

    const loadAssignments = async () => {
      if (
        isFetching ||
        !selectedSpaceId ||
        !user ||
        !requested.current ||
        resolvingToken.current
      ) {
        return;
      }
      const currentGeneration = generation.current;
      resolvingToken.current = true;
      requested.current = false;
      try {
        const accessToken = await resolveAccessToken(selectedSpaceId);
        if (generation.current !== currentGeneration) return;
        if (accessToken) {
          latest.current.fetchAssignments({
            user: latest.current.user,
            accessToken,
            spaceId: selectedSpaceId,
          });
        }
      } finally {
        if (generation.current === currentGeneration) {
          resolvingToken.current = false;
          if (requested.current) setRefreshVersion((value) => value + 1);
        }
      }
    };

    loadAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSpaceId, isFetching, user, refreshVersion]);

  return requestRefresh;
};

export default useFetchAssignments;
